/**
 * Estimate Drawer Component
 * BuilderBel Construction Project Management System
 *
 * Drawer for creating/editing estimates with basic info form
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { X, FileSpreadsheet } from 'lucide-react';
import { DatePicker } from '../forms';
import type {
  Estimate,
  CreateEstimateRequest,
  UpdateEstimateRequest,
  EstimateType,
} from '../../types/estimate';

export interface EstimateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEstimateRequest | UpdateEstimateRequest) => void;
  estimate?: Estimate | null;
  projectId: string;
  isLoading?: boolean;
}

interface FormData {
  name: string;
  description: string;
  estimateType: EstimateType;
  notes: string;
  tags: string;
  effectiveDate: string;
  expirationDate: string;
}

const estimateTypeOptions: { value: EstimateType; label: string; description: string }[] = [
  {
    value: 'preliminary',
    label: 'Preliminary',
    description: 'Initial rough estimate based on limited information',
  },
  {
    value: 'budget',
    label: 'Budget',
    description: 'Estimate used for budgeting and planning purposes',
  },
  {
    value: 'detailed',
    label: 'Detailed',
    description: 'Comprehensive estimate with full line item breakdown',
  },
  {
    value: 'change_order',
    label: 'Change Order',
    description: 'Estimate for scope changes or modifications',
  },
  {
    value: 'final',
    label: 'Final',
    description: 'Final approved estimate for contract purposes',
  },
];

const EstimateDrawer: React.FC<EstimateDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  estimate,
  projectId,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      estimateType: 'detailed',
      notes: '',
      tags: '',
      effectiveDate: '',
      expirationDate: '',
    },
  });

  useEffect(() => {
    if (estimate) {
      reset({
        name: estimate.name,
        description: estimate.description || '',
        estimateType: estimate.estimateType,
        notes: estimate.notes || '',
        tags: estimate.tags?.join(', ') || '',
        effectiveDate: estimate.estimateDate
          ? new Date(estimate.estimateDate).toISOString().split('T')[0]
          : '',
        expirationDate: estimate.validUntil
          ? new Date(estimate.validUntil).toISOString().split('T')[0]
          : '',
      });
    } else {
      reset({
        name: '',
        description: '',
        estimateType: 'detailed',
        notes: '',
        tags: '',
        effectiveDate: '',
        expirationDate: '',
      });
    }
  }, [estimate, reset]);

  const handleFormSubmit = (data: FormData) => {
    const tags = data.tags
      ? data.tags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean)
      : undefined;

    if (estimate) {
      // Update
      const updateData: UpdateEstimateRequest = {
        name: data.name,
        description: data.description || undefined,
        estimateType: data.estimateType,
        estimateDate: data.effectiveDate || undefined,
        validUntil: data.expirationDate || undefined,
      };
      onSubmit(updateData);
    } else {
      // Create
      const createData: CreateEstimateRequest = {
        projectId,
        name: data.name,
        description: data.description || undefined,
        estimateType: data.estimateType,
        estimateDate: data.effectiveDate || undefined,
        validUntil: data.expirationDate || undefined,
      };
      onSubmit(createData);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] max-w-2xl animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-50 rounded-lg">
                <FileSpreadsheet className="h-5 w-5 text-brand-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                {estimate ? 'Edit Estimate' : 'Create New Estimate'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimate Name *
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'Name is required' })}
                  placeholder="e.g., Building A - Phase 1 Estimate"
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  {...register('description')}
                  rows={3}
                  placeholder="Brief description of the estimate scope..."
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                />
              </div>

              {/* Estimate Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Estimate Type *
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {estimateTypeOptions.map(option => (
                    <label
                      key={option.value}
                      className="relative flex items-start p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 has-[:checked]:border-brand-500 has-[:checked]:bg-brand-50"
                    >
                      <input
                        type="radio"
                        value={option.value}
                        {...register('estimateType')}
                        className="mt-1 h-4 w-4 text-brand-600 border-gray-300 focus:ring-brand-500"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          {option.label}
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          {option.description}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name="effectiveDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Effective Date"
                      value={field.value || ''}
                      onChange={date => field.onChange(date || '')}
                    />
                  )}
                />
                <Controller
                  name="expirationDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Expiration Date"
                      value={field.value || ''}
                      onChange={date => field.onChange(date || '')}
                    />
                  )}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <input
                  type="text"
                  {...register('tags')}
                  placeholder="Comma-separated tags (e.g., phase1, interior, renovation)"
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  {...register('notes')}
                  rows={4}
                  placeholder="Additional notes, assumptions, or exclusions..."
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : estimate ? 'Update Estimate' : 'Create Estimate'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default EstimateDrawer;
